import { type TypedDocumentNode } from '@graphql-typed-document-node/core'
import { isStaging } from '@lib/isStaging'
import { eaChannelMap, SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME } from 'config'

import { print } from 'graphql'
import { getToken } from 'next-auth/jwt'
import { getSession } from 'next-auth/react'

export async function getDiscountToken() {
  let discountToken: string | null = null

  try {
    if (typeof window === 'undefined') {
      // Delay dynamic import till runtime if truly on the server ssrRequest
      const { ssrRequest } = await import('lib/ssr-req-storage')
      const req = ssrRequest()
      discountToken = req.cookies[SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME]
    } else {
      const cookies = (await import('js-cookie'))?.default ?? null
      discountToken = cookies?.get(SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME)
    }
  } catch (error) {
    //this is expected if it is not wrapped by a request context
    // console.log(error)
  }

  return discountToken
}

export function isDiscountTokenValid(discountToken: string | null) {
  // For now, we assume that a valid discount token is a non-empty string
  // In the future, this could be replaced with a more complex validation logic
  return (
    discountToken && discountToken === '630110d7-84bc-43be-b18e-f8e0af67a746'
  )
}

function applyDiscountChannelOverride<TVariables>(
  variables: TVariables | undefined,
  discountToken: string | null,
): TVariables | undefined {
  if (
    !isDiscountTokenValid(discountToken) ||
    !variables ||
    typeof variables !== 'object'
  ) {
    return variables
  }
  const vars = { ...(variables as any) }

  // override root‐level channelId
  if (typeof vars.channelId === 'string') {
    vars.channelId = eaChannelMap[vars.channelId] ?? vars.channelId
  }

  // override nested input.channelId
  if (
    vars.input &&
    typeof vars.input === 'object' &&
    typeof (vars.input as any).channelId === 'string'
  ) {
    const current = (vars.input as any).channelId
    vars.input = {
      ...(vars.input as Record<string, any>),
      channelId: eaChannelMap[current] ?? current,
    }
  }

  return vars as TVariables
}

export async function execute<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  ...[variables]: TVariables extends Record<string, never> ? [] : [TVariables?]
) {
  let session = null
  let discountToken = null
  try {
    if (typeof window === 'undefined') {
      // Delay dynamic import till runtime if truly on the server ssrRequest
      const { ssrRequest } = await import('lib/ssr-req-storage')
      const req = ssrRequest()
      discountToken =
        req.cookies[SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME] ?? req.cookies

      session = await getToken({ req })
      // session = await getSession({ req })
    } else {
      const cookies = (await import('js-cookie'))?.default ?? null
      session = await getSession()
      discountToken = cookies?.get(SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME)
    }
  } catch (error) {
    //this is expected if it is not wrapped by a request context
    // console.log(error)
  }

  const operationVariables =
    applyDiscountChannelOverride(variables, discountToken) ?? variables

  const endpoint = process.env.NEXT_PUBLIC_SUBLIME_ECOM!
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/graphql-response+json',
      'x-discount-token': discountToken ? discountToken : '',
      'x-preview-token': isStaging() ? 'draft' : '',
      'GraphQL-Preflight': '1',
      authorization: session?.accessToken
        ? `Bearer ${session.accessToken}`
        : '',
    },
    body: JSON.stringify({
      query: print(document),
      variables: operationVariables,
    }),
  })

  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch: ${response.statusText}. Body: ${await response.text()}`,
    )
  }

  let rawResponse = await response.text()
  rawResponse = rawResponse.replace(
    // temporary fix for unauthenticated errors
    '"code":AUTH_NOT_AUTHENTICATED',
    '"code":"AUTH_NOT_AUTHENTICATED"',
  )

  try {
    const jsonResponse = JSON.parse(rawResponse)
    return jsonResponse.data as TResult
  } catch (error) {
    throw new Error(
      `Failed to parse JSON response: ${error}. Body: ${rawResponse}`,
    )
  }
}

export async function executeWithAuth<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  customAuth: string,
  ...[variables]: TVariables extends Record<string, never> ? [] : [TVariables?]
) {
  let session = null
  let discountToken = null
  try {
    if (typeof window === 'undefined') {
      // Delay dynamic import till runtime if truly on the server ssrRequest
      const { ssrRequest } = await import('lib/ssr-req-storage')
      const req = ssrRequest()
      discountToken =
        req.cookies[SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME] ?? req.cookies

      session = await getSession({ req })
    } else {
      const cookies = (await import('js-cookie'))?.default ?? null
      session = await getSession()
      discountToken = cookies?.get(SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME)
    }
  } catch (error) {
    //this is expected if it is not wrapped by a request context
    // console.log(error)
  }

  const endpoint = process.env.NEXT_PUBLIC_SUBLIME_ECOM!

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/graphql-response+json',
      'x-discount-token': discountToken ? discountToken : '',
      'x-preview-token': isStaging() ? 'draft' : '',
      'GraphQL-Preflight': '1',
      authorization: customAuth ? `Bearer ${customAuth}` : '',
    },
    body: JSON.stringify({
      query: print(document),
      variables,
    }),
  })

  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch: ${response.statusText}. Body: ${await response.text()}`,
    )
  }

  const jsonResponse = await response.json()

  return jsonResponse.data as TResult
}
